/**
 * @description 根据原始图片CDN地址及裁剪参数配置动态计算需要获取的裁剪后的图片CDN地址；
 * @note SSR模式下，由于无法动态获取window宽度，需要手动传入合适的devicePixelRatio
 */

import type { StaticImageData } from 'next/image';
import Url from 'url-parse';
import { CropConfigs, OptionsType, WebpDetectFn } from '../../types';
import { CDNWhiteList, ESTypes, EXACT_FORMAT_MAP, FORMAT_MAP } from '../constants/constants';
import ImageStandards from '../constants/imageStandards';
import { getDevicePixelRatio } from './getDevicePixelRatio';
import isWebpSupport from './isWebpSupport';
import { getNumFromPercentage, prototypeToString } from './tools';

export type GetCroppedImageUrl = (
  originUrl: string | StaticImageData,
  cropConfigs?: string | CropConfigs,
  options?: string | OptionsType
) => string;

const IMAGEMAXWIDTH = 1024;
const IMAGEMAXHEIGHT = 1024;
const MINDEVICEPIXELRATIO = 1;

const SupportedFormatList = Object.values(FORMAT_MAP);
const PicCroppedExg = /![\S]*\.(jpg|jpeg|png|tiff|webp)/;

const getOriginalFormat = (pathname: string) => {
  const PicFormatExg = /\/[^\/\s\.]+\.(jpg|jpeg|png|tiff)/;
  const result = PicFormatExg.exec(pathname);
  if (result === null) {
    return null;
  }
  return result[1] || FORMAT_MAP.JPEG;
};

const getAutoFormat = (pathname: string, webpDetectFn: WebpDetectFn) => {
  if (webpDetectFn?.()) return FORMAT_MAP.WEBP;
  return getOriginalFormat(pathname);
};

const getFormat = ({
  format,
  pathname,
  webpDetectFn
}: {
  format: string;
  pathname: string;
  webpDetectFn: WebpDetectFn;
}) => {
  // 传入不支持的format时使用默认format
  const _format = SupportedFormatList.includes(format) ? format : FORMAT_MAP.AUTO;
  if (Object.values(EXACT_FORMAT_MAP).includes(_format)) {
    return _format;
  }
  if (_format === FORMAT_MAP.ORIGINAL) {
    return getOriginalFormat(pathname);
  }

  return getAutoFormat(pathname, webpDetectFn);
};

/**
 * 获取需要通过CDN裁剪处理的图片URL（拼接了裁剪处理参数）
 * @param originUrl 图片原始URL（不包含裁剪参数）
 * @param configs 图片裁剪参数集合（可传字符串或CropConfigs类型；传入字符串，则去通用规则设置里面去取）
 * @param webpDetect: 可传入：boolean [是否支持webp]; function [是否支持webp]; string [req.header.accept]
 * @returns 拼接了裁剪参数的图片URL
 */
export const getCroppedImageUrl: GetCroppedImageUrl = (originUrl, configs = {}, options) => {
  let _url = null;
  try {
    _url = new Url(originUrl);
  } catch (error) {
    return originUrl;
  }

  if (!CDNWhiteList.includes(_url.host)) {
    return originUrl;
  }

  // 如果图片被裁剪过，则不做处理
  if (PicCroppedExg.test(_url.pathname)) {
    return originUrl;
  }

  const _options = typeof options === 'string' ? { reqHeaderAccept: options as string } : (options as OptionsType);

  const { webpDetect = isWebpSupport, reqHeaderAccept } = _options ?? {};

  let renderWidth: string | number = 'auto',
    renderHeight: string | number = 'auto',
    quality,
    format = FORMAT_MAP.AUTO,
    defaultDevicePixelRatio = MINDEVICEPIXELRATIO,
    devicePixelRatio;

  // 如果configs为string，则去通用规则设置里面去取裁剪参数
  if (prototypeToString(configs) === ESTypes.string) {
    const standard = configs as string;
    if (ImageStandards.hasOwnProperty(standard)) {
      ({
        renderWidth = 'auto',
        renderHeight = 'auto',
        quality,
        format = FORMAT_MAP.AUTO,
        defaultDevicePixelRatio = MINDEVICEPIXELRATIO,
        devicePixelRatio
      } = ImageStandards[standard] ?? {});
    }
  }

  // 如果configs为object，则从configs中获取裁剪参数
  if (prototypeToString(configs) === ESTypes.object) {
    ({
      renderWidth = 'auto',
      renderHeight = 'auto',
      quality,
      format = FORMAT_MAP.AUTO,
      defaultDevicePixelRatio = MINDEVICEPIXELRATIO,
      devicePixelRatio
    } = (configs as CropConfigs) ?? {});
  }

  const finalFormat = getFormat({
    format,
    pathname: _url.pathname,
    webpDetectFn: () => webpDetect?.(reqHeaderAccept)
  });

  if (!finalFormat) return originUrl;

  // 设备DPI（SSR模式下，由于无法动态获取window宽度，需要手动传入合适的devicePixelRatio）
  const _devicePixelRatio =
    !devicePixelRatio || devicePixelRatio < MINDEVICEPIXELRATIO
      ? getDevicePixelRatio(defaultDevicePixelRatio)
      : devicePixelRatio;

  // 裁剪参数中宽度、高度默认为0（即按CDN中的裁剪规则处理）
  let _width = 0,
    _height = 0;

  if (prototypeToString(renderWidth) === ESTypes.number) {
    const _renderWidth = renderWidth as number;
    _width = _renderWidth < 0 ? 0 : _renderWidth;
  }

  if (prototypeToString(renderHeight) === ESTypes.number) {
    const _renderHeight = renderHeight as number;
    _height = _renderHeight < 0 ? 0 : _renderHeight;
  }

  // 若renderWidth为百分比值，则根据document.documentElement.clientWidth计算图片实际宽度
  if (prototypeToString(renderWidth) === ESTypes.string && renderWidth !== 'auto') {
    const clientWidth = globalThis?.document?.documentElement?.clientWidth || 0;
    _width = getNumFromPercentage(clientWidth, renderWidth as string);
  }

  // 若renderHeight为百分比值，则根据document.documentElement.clientHeight计算图片实际高度
  if (prototypeToString(renderHeight) === ESTypes.string && renderHeight !== 'auto') {
    const clientHeight = globalThis?.document?.documentElement?.clientHeight || 0;
    _height = getNumFromPercentage(clientHeight, renderHeight as string);
  }

  // 裁剪参数中的宽度、高度目前不支持小数值、宽度不超过1024
  _width = Math.floor(_width * _devicePixelRatio);
  _width = _width > IMAGEMAXWIDTH ? IMAGEMAXWIDTH : _width;
  _height = Math.floor(_height * _devicePixelRatio);
  _height = _height > IMAGEMAXHEIGHT ? IMAGEMAXHEIGHT : _height;

  // quality不支持负数和小数
  if (prototypeToString(quality) === ESTypes.number) {
    quality = (quality as number) < 0 ? void 0 : Math.floor(quality as number);
  }

  _url.set('pathname', `${_url.pathname}!c${_width}x${_height}${quality ? `_q${quality}` : ''}.${finalFormat}`);

  return _url.toString();
};
export type { CropConfigs };
